/* @flow */

import * as React from 'react'
import styled from 'styled-components'
import sumBy from 'lodash/sumBy'
import { Link } from 'react-router-dom'
import { original } from 'immer'

import FormatCurrency from '../../../infrastructure/components/FormatCurrency'
import DropDeliveryDate from '../../orders/components/DropDeliveryDate'
import ProductTable, {
  CustomFieldColumn,
  ToggleVariantsAction,
  LineText,
  UnitPrice,
  dataSourceConfigurations,
  rowConfigurations,
  columnConfigurations,
  productDataHeaderConfigs,
  wrapRowsInBatchControl,
  tableSectionConfigurations,
} from '../../products/components/ProductTable2'
import { getWebshopAvailability, getWebshopPrices } from '../api'
import { WebshopContext } from '../shared'
import { UserHasPermissions } from '../../../infrastructure/components/Authorization'

import { getOrderPrices } from '../../orders/api'
import type { ConnectedBrand } from '../../types'
import { SessionContext, createConfiguredVariantId } from '../../shared'

type Props = {
  deleteAction?: boolean,
}

const WebshopProductTable = ({
  currency,
  deleteAction = false,
  deliveryStatus,
  lines,
  prices,
  webshopSession,
  ...props
}: Props) => {
  const { entity, user } = React.useContext(SessionContext)
  const { brand, deliveryLines, drops, isMobile } =
    React.useContext(WebshopContext)

  const showQuantity =
    entity.entity_type !== 'shop' ||
    brand.settings.stock_settings.show_quantity === true

  const rows = React.useMemo(() => {
    return createRows(brand, deleteAction, showQuantity)
  }, [brand, deleteAction, showQuantity])

  const columns = React.useMemo(() => {
    return createColumns(brand, user)
  }, [brand, user])

  const context = React.useMemo(() => {
    return {
      currency: webshopSession.currency,
      customer: webshopSession.customer,
      webshopSession: webshopSession,
    }
  }, [currency, webshopSession])

  const dataSources = React.useMemo(() => {
    return createDataSources(
      deliveryLines,
      drops,
      deliveryStatus,
      prices,
      showQuantity
    )
  }, [deliveryLines, drops, deliveryStatus, prices, showQuantity])

  const tableSections = React.useMemo(() => {
    return tableSectionConfigurations.delivery_lines()
  }, [])

  return (
    <ProductTable
      columns={columns}
      context={context}
      data={dataSources}
      defaultRowData={defaultRowData}
      isMobile={isMobile}
      lines={lines}
      mode="product_table"
      productDataOverrides={productDataOverrides}
      productImage={false}
      rows={rows}
      productImage={false}
      showNoosLabel
      tableSettings={false}
      tableSections={
        /*
        We used to to also pass `undefined` if webshopSession.drop is set. However,
        this causes problems in the scenario where you do not have multi-drop AND
        use drop deliveries. Because then the lines are not divided correctly into their
        drop deliveries
        */
        brand.settings.webshop_settings.use_drops ? tableSections : undefined
      }
      tableSettings={false}
      useOutsideProductCache
      {...props}
    />
  )
}

export default WebshopProductTable

const defaultRowData = {
  quantity: 0,
}

const createDataSources = (
  deliveryLines,
  rops,
  deliveryStatus,
  prices,
  showQuantity
) => {
  const dataSources = {
    availability: {
      data_level: 'variant',
      resolve: () => Promise.resolve(deliveryStatus),
    },
    delivery_lines: {
      data_level: 'table',
      resolve: () => Promise.resolve(deliveryLines),
    },
    drops: {
      data_level: 'table',
      resolve: () => Promise.resolve(deliveryLines),
    },
    pricing: {
      data_level: 'product',
      resolve: () => Promise.resolve(prices),
    },
    /*pricing: {
      data_level: 'product',
      resolve: ({ context, event }) => {
        const { missing_config_variants, missing_products } = event

        return getWebshopPrices(
          context.webshopSession.brand_id,
          context.webshopSession.id,
          missing_products.map(p => p.id)
        ).then(response => {
          if (response.error) {
            return []
          }

          return response.payload.prices
        })
      },
    },*/
  }

  return dataSources
}

const productDataOverrides = {
  item_number: {
    shopUrl: true,
  },
}

const createColumns = (brand, user) => {
  let canMakeDiscounts = false
  if (
    brand &&
    user &&
    [10919, 295818, 430254].includes(brand.id) &&
    brand.id === user.entity_id
  ) {
    canMakeDiscounts = UserHasPermissions(
      user,
      ['orders:discounts:percentage', 'orders:discounts:monetary'],
      true
    )
  }

  const columns = [
    columnConfigurations.rec_sales_price({
      rows: ['quantity'],
    }),
    columnConfigurations.total_quantity({
      colli_not_included_in_value: true,
      rows: ['quantity'],
    }),
    columnConfigurations.unit_price({
      aggregate_product: false,
      editable: canMakeDiscounts,
      employeeDiscount: true,
      // since employee discount is a separate button we do not want
      // to be able to edit it
      employeeDiscountEditable: false,
      mapPropertiesOnModalSave: (lineUpdate, modalFormValues) => {
        return {
          ...lineUpdate,

          custom_discount:
            lineUpdate.discount_method === 'price'
              ? lineUpdate.net_price
              : lineUpdate.discount_percentage,
          custom_discount_mode: lineUpdate.discount_method,
        }
      },
      rows: ['quantity'],
      v2: true,
    }),
    columnConfigurations.total_price({
      colli_not_included_in_value: true,
      rows: ['quantity'],
    }),
  ]

  return columns
}

const createRows = (
  brand: ConnectedBrand,
  deleteAction: boolean,
  showQuantity
) => {
  const rows = [
    {
      actions: deleteAction
        ? [
            {
              key: 'delete_lines',
              render: ({
                lines,
                row,
                splitsDispatcher,
                subSectionKey,
                updateLines,
              }) => {
                const onClick = () => {
                  updateLines(
                    {
                      lines: lines.map(line => ({ ...line, delete: true })),
                      type: 'update',
                    },
                    row,
                    subSectionKey
                  )
                }

                return (
                  <button
                    type="button"
                    className="btn btn-white btn-xs"
                    onClick={onClick}
                  >
                    <span className="glyphicon glyphicon-remove" />
                  </button>
                )
              },
            },
          ]
        : [],
      colli_not_included_in_value: true,
      editable: true,
      enable_colli: true,
      expand_assortments: true,
      key: 'quantity',
      label: 'Quantity',
      map_edit: ({ data, lines }) => {
        let quantity = parseInt(data.quantity)
        if (isNaN(quantity)) {
          quantity = 0
        }

        return {
          ...data,
          quantity,
        }
      },
      splittable: false,
    },
  ]

  if (showQuantity) {
    rows.push(
      rowConfigurations.availability({
        defaultRender: () => '-',
        isCustomerFacingDeliveryStatus: true,
      })
    )
  }

  return wrapRowsInBatchControl(brand, rows)
}
